"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { BattleSprite } from "@/components/game/BattleSprite";
import { BattleHPBar } from "@/components/game/BattleHPBar";
import { PixelText } from "@/components/ui/pixel-text";

export type PartyMember = {
  speciesId: string;
  nickname?: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  fainted: boolean;
  active: boolean;
};

type Props = {
  party: PartyMember[];
  enabled: boolean;
  onSwitch: (index: number) => void;
  onBack: () => void;
};

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const GOLD = "#c5a83a";
const RED = "#b04848";
const PLACEHOLDER_BG = "#2a2620";
const PLACEHOLDER_BORDER = "#3a342c";

const COLS = 2;
const ROWS = 3;
const SLOTS = COLS * ROWS;

export function BattleParty({ party, enabled, onSwitch, onBack }: Props) {
  const [cursor, setCursor] = useState(0);

  const slots = useMemo(() => {
    const padded: (PartyMember | null)[] = Array.from({ length: SLOTS }, () => null);
    for (let i = 0; i < Math.min(party.length, SLOTS); i += 1) {
      padded[i] = party[i] ?? null;
    }
    return padded;
  }, [party]);

  const isDisabled = useCallback(
    (i: number) => {
      const m = slots[i];
      return !m || m.fainted || m.active;
    },
    [slots]
  );

  const move = useCallback((dx: number, dy: number) => {
    setCursor((c) => {
      const col = c % COLS;
      const row = (c - col) / COLS;
      const nextCol = Math.max(0, Math.min(COLS - 1, col + dx));
      const nextRow = Math.max(0, Math.min(ROWS - 1, row + dy));
      return nextRow * COLS + nextCol;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "Enter" ||
        key === "z" ||
        key === "Z" ||
        key === "x" ||
        key === "X" ||
        key === "Escape"
      ) {
        e.preventDefault();
      }

      switch (key) {
        case "ArrowUp":
          move(0, -1);
          break;
        case "ArrowDown":
          move(0, 1);
          break;
        case "ArrowLeft":
          move(-1, 0);
          break;
        case "ArrowRight":
          move(1, 0);
          break;
        case "Enter":
        case "z":
        case "Z":
          if (!isDisabled(cursor)) {
            onSwitch(cursor);
          }
          break;
        case "x":
        case "X":
        case "Escape":
          onBack();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, cursor, move, onSwitch, onBack, isDisabled]);

  const dimmed = !enabled;

  return (
    <div
      className={`flex flex-col gap-3 ${dimmed ? "opacity-50 pointer-events-none" : ""}`}
      style={{
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "1rem 1.25rem",
        imageRendering: "pixelated",
      }}
    >
      <div className="flex items-center justify-between">
        <PixelText
          as="div"
          glow="none"
          size="md"
          className="!text-[#1a1a1a] !tracking-normal !normal-case"
        >
          Choose a Surggie
        </PixelText>
        <PixelText
          as="div"
          glow="none"
          size="sm"
          className="!text-[#1a1a1a] tracking-wider"
        >
          X / Esc: Back
        </PixelText>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
        role="menu"
        aria-label="Party"
      >
        {slots.map((member, i) => {
          const selected = i === cursor;
          const disabled = isDisabled(i);

          if (!member) {
            return (
              <div
                key={`empty-${i}`}
                aria-hidden
                style={{
                  backgroundColor: PLACEHOLDER_BG,
                  border: `2px solid ${selected ? INK : PLACEHOLDER_BORDER}`,
                  minHeight: 96,
                  imageRendering: "pixelated",
                }}
              />
            );
          }

          const displayName = member.nickname?.trim() || member.speciesId;
          const statusLabel = member.fainted
            ? "FAINTED"
            : member.active
              ? "ACTIVE"
              : null;
          const statusColor = member.fainted ? RED : GOLD;

          return (
            <button
              key={`${member.speciesId}-${i}`}
              type="button"
              role="menuitem"
              aria-disabled={disabled}
              onMouseEnter={() => setCursor(i)}
              onClick={() => {
                if (!disabled) onSwitch(i);
              }}
              className="relative text-left"
              style={{
                backgroundColor: selected ? INK : CREAM,
                color: selected ? CREAM : INK,
                border: `2px solid ${INK}`,
                padding: "0.5rem 0.625rem",
                transition: "none",
                imageRendering: "pixelated",
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                display: "flex",
                gap: "0.625rem",
                alignItems: "center",
              }}
            >
              <div className="shrink-0">
                <BattleSprite
                  speciesId={member.speciesId}
                  orientation="front"
                  scale={2}
                  battlePortrait
                />
              </div>

              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="font-mono font-bold uppercase tracking-wider truncate"
                    style={{
                      fontSize: "0.85rem",
                      lineHeight: 1,
                      color: selected ? CREAM : INK,
                    }}
                  >
                    {displayName}
                  </span>
                  <span
                    className="font-mono font-bold tracking-wider tabular-nums"
                    style={{
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      color: selected ? CREAM : INK,
                    }}
                  >
                    Lv{member.level}
                  </span>
                </div>

                <BattleHPBar
                  current={member.hpCurrent}
                  max={member.hpMax}
                  width={120}
                  showNumeric={true}
                />

                {statusLabel ? (
                  <span
                    className="font-mono font-bold uppercase tracking-widest self-start"
                    style={{
                      fontSize: "0.6875rem",
                      lineHeight: 1,
                      color: CREAM,
                      backgroundColor: statusColor,
                      border: `2px solid ${INK}`,
                      padding: "0.1875rem 0.375rem",
                    }}
                  >
                    {statusLabel}
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default BattleParty;
